import { motion } from "framer-motion";
import { ChapterShell } from "../ChapterShell";
import { FadeUp } from "../Reveal";
import { COMPETENCIES, HEADSHOT_FALLBACK } from "../../data/content";
import { imgFallback } from "../../utils";

const NETWORK = ["Marketing", "Sales", "Medical", "Finance", "Supply", "Doctors", "Patients"];

export const ChapterEight = () => (
  <ChapterShell
    n="08"
    id="chapter-8"
    era="Leadership"
    title="Leading People & Business"
    quote="Strategy becomes powerful when people execute it together."
  >
    <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
      <div className="lg:col-span-6">
        <div className="flex flex-wrap gap-6">
          {[
            { v: "3", l: "Product Managers" },
            { v: "7", l: "Sales Personnel" },
          ].map((s, i) => (
            <FadeUp key={s.l} delay={i * 0.12} className="glass rounded-3xl px-8 py-6">
              <p className="font-display text-5xl font-black text-[#0A192F]">{s.v}</p>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#0A192F]/50">{s.l}</p>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.2} className="relative mx-auto mt-14 max-w-md">
          <div className="relative flex h-64 items-center justify-center">
            <motion.span
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ type: "spring", stiffness: 120 }}
              className="z-10 flex h-24 w-24 items-center justify-center rounded-full bg-[#0A192F] text-center font-display text-[10px] font-black uppercase tracking-[0.14em] text-[#00E5FF] shadow-[0_0_50px_rgba(0,229,255,0.35)]"
            >
              GM Marketing
            </motion.span>
            {NETWORK.map((node, i) => {
              const angle = (i / NETWORK.length) * Math.PI * 2 - Math.PI / 2;
              const x = Math.cos(angle) * 44;
              const y = Math.sin(angle) * 38;
              return (
                <motion.span
                  key={node}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: 0.3 + i * 0.09, type: "spring", stiffness: 140 }}
                  className="glass absolute rounded-full px-4 py-2 font-display text-[10px] font-bold uppercase tracking-[0.14em] text-[#0A192F]"
                  style={{ left: `calc(50% + ${x}%)`, top: `calc(50% + ${y}%)`, transform: "translate(-50%, -50%)" }}
                >
                  {node}
                </motion.span>
              );
            })}
          </div>
        </FadeUp>

        <FadeUp delay={0.25} className="mt-10 flex flex-wrap gap-3">
          {COMPETENCIES.map((c) => (
            <span
              key={c}
              className="rounded-full border border-[#0A192F]/15 bg-white/60 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-[#0A192F]/70 transition-transform duration-300 hover:-translate-y-1"
            >
              {c}
            </span>
          ))}
        </FadeUp>
      </div>

      <div className="lg:col-span-6">
        <FadeUp delay={0.15} className="relative mx-auto max-w-xs">
          <div className="absolute -inset-5 rounded-[2rem] bg-gradient-to-br from-[#00E5FF]/20 to-[#00BFA5]/20 blur-xl" />
          <div className="glass relative overflow-hidden rounded-[1.8rem] p-2">
            <img
              src="/assets/avatar-audience.png"
              onError={imgFallback(HEADSHOT_FALLBACK)}
              alt="Speaking at a national conference"
              data-testid="chapter8-avatar"
              className="w-full rounded-[1.4rem] object-cover"
            />
          </div>
        </FadeUp>

        <FadeUp delay={0.25} className="mt-10 rounded-[2rem] border border-[#0A192F]/10 bg-white p-8">
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#0A192F]/45">
            Conference & Event Leadership
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <span className="rounded-xl bg-[#0A192F] px-5 py-3 font-display text-lg font-black text-white">USICON</span>
            <span className="rounded-xl bg-[#0A192F] px-5 py-3 font-display text-lg font-black text-white">AUA</span>
          </div>
          <p className="mt-5 text-sm leading-relaxed text-[#4A5568]">
            Organized 12 national USICON conferences and drove participation at the American Urological Association
            conference for <strong className="text-[#0A192F]">7 consecutive years — AUA 2010 to AUA 2017</strong>.
          </p>
        </FadeUp>
      </div>
    </div>
  </ChapterShell>
);
